import type { EvExportProfile } from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Download, FileDown, Pencil, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExportProfileDialog, ExportRunDialog } from "../../../features/ev/export-dialogs.js";
import { useEvExportProfiles, useEvPermissions } from "../../../features/ev/queries.js";
import { EvPage, TableFrame, Th, useEvStation } from "../../../features/ev/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/sarj/disa-aktarim")({
  component: ExportsRoute,
});

/**
 * Düzenleyici dışa aktarım (Prompt 19 web, #74): profil listesi (`GET /ev/export-profiles`; `ev.manage` ya da
 * `ev.report.read`), profil oluştur / düzenle (KİRACI kapsamlı `ev.manage` — düğmeler sunucunun `canEdit`'ine göre) ve
 * "dışa aktar" (`GET /ev/exports/sessions`, `ev.report.read`) — dosya indirme. Biçim resmî bir düzenleyici biçimi
 * DEĞİLDİR: alanlar kapalı listeden seçilir, düzenleyicinin istediğine göre profilde düzenlenir (doğrulanmalı).
 */
function ExportsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  const open = permissions.enabled && permissions.canSeeExports;
  const profiles = useEvExportProfiles(open);
  const selection = useEvStation(open && permissions.canRead);
  const [dialog, setDialog] = useState<
    { kind: "profile"; profile: EvExportProfile | null } | { kind: "run"; profileId: string | null } | null
  >(null);
  const data = profiles.data;
  const canRun = permissions.canReadReports && (data?.items.some((profile) => profile.isActive) ?? false);

  return (
    <EvPage
      title={t("menu.evExports")}
      description={t("ev.exports.description")}
      requirement="exports"
      actions={
        data ? (
          <div className="flex flex-wrap gap-2">
            {canRun ? (
              <Button onClick={() => setDialog({ kind: "run", profileId: null })}>
                <Download aria-hidden="true" />
                {t("ev.exports.run")}
              </Button>
            ) : null}
            {data.canEdit ? (
              <Button variant="outline" onClick={() => setDialog({ kind: "profile", profile: null })}>
                <Plus aria-hidden="true" />
                {t("ev.exports.addProfile")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      <Alert tone="info" title={t("ev.exports.notOfficialTitle")}>
        <span data-testid="ev-export-not-official">{t("ev.exports.notOfficial")}</span>
      </Alert>
      {profiles.error ? (
        <Alert tone="danger">{errorMessage(profiles.error)}</Alert>
      ) : !data ? (
        <SkeletonRows rows={4} />
      ) : data.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<FileDown />}
          title={t("ev.exports.emptyTitle")}
          description={t("ev.exports.emptyDescription")}
        />
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2" aria-label={t("ev.exports.listLabel")}>
          {data.items.map((profile) => (
            <li key={profile.id}>
              {/* Kısa profil kartı komşusu kadar uzasa da tablo başlığın altında, düğmeler dipte kalır. */}
              <Card
                className="grid h-full grid-rows-[auto_auto_auto_1fr] gap-3 p-4"
                aria-labelledby={`profile-${profile.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="grid gap-0.5">
                    <h2 id={`profile-${profile.id}`} className="text-base font-semibold">
                      {profile.name}
                    </h2>
                    <span className="font-mono text-xs text-muted-foreground">{profile.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <StatusBadge tone="info">{profile.format}</StatusBadge>
                    <StatusBadge tone={profile.isActive ? "success" : "neutral"}>
                      {profile.isActive ? t("common.active") : t("common.inactive")}
                    </StatusBadge>
                  </div>
                </div>
                <TableFrame label={t("ev.exports.columnsOf", { name: profile.name })}>
                  <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <Th align="right">#</Th>
                      <Th>{t("ev.exports.headerColumn")}</Th>
                      <Th>{t("ev.exports.fieldKey")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.fields.map((field, index) => (
                      <tr key={field.key} className="border-t">
                        <td className="px-3 py-1 text-right tabular text-xs">{index + 1}</td>
                        <td className="px-3 py-1 text-sm">{field.label}</td>
                        <td className="px-3 py-1 font-mono text-xs text-muted-foreground">{field.key}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableFrame>
                <p className="text-xs text-muted-foreground">
                  {t("ev.exports.meta", {
                    count: profile.fields.length,
                    at: formatDateTime(profile.updatedAt),
                  })}
                </p>
                <div className="flex flex-wrap gap-2 self-end border-t pt-3">
                  {permissions.canReadReports && profile.isActive ? (
                    <Button
                      size="sm"
                      onClick={() => setDialog({ kind: "run", profileId: profile.id })}
                      aria-label={t("ev.exports.runFor", { name: profile.name })}
                    >
                      <Download aria-hidden="true" />
                      {t("ev.exports.run")}
                    </Button>
                  ) : null}
                  {data.canEdit ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDialog({ kind: "profile", profile })}
                      aria-label={t("ev.exports.editFor", { name: profile.name })}
                    >
                      <Pencil aria-hidden="true" />
                      {t("common.edit")}
                    </Button>
                  ) : null}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {data ? (
        <>
          <ExportProfileDialog
            open={dialog?.kind === "profile"}
            onOpenChange={(next) => !next && setDialog(null)}
            profile={dialog?.kind === "profile" ? dialog.profile : null}
            available={data.availableFields}
          />
          <ExportRunDialog
            open={dialog?.kind === "run"}
            onOpenChange={(next) => !next && setDialog(null)}
            profiles={data.items}
            stations={selection.stations}
            defaultProfileId={dialog?.kind === "run" ? dialog.profileId : null}
          />
        </>
      ) : null}
    </EvPage>
  );
}
